import EmptyState from "@/components/EmptyState";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Plus, Copy } from "lucide-react";
import PlatformBadge from "@/components/PlatformBadge";
import MediaImage from "@/components/MediaImage";
import { explicitDemoMedia } from "@/lib/media";
import { timeAgo } from "@/lib/format";

export default function SwipeFile({ swipes, onChange }) {
  const { canEdit } = useAuth();
  const [hook, setHook] = useState("");
  const [pattern, setPattern] = useState("");
  const [niche, setNiche] = useState("");

  const add = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    try {
      await api.post("/patterns/swipes", { hook_text: hook, pattern, niche });
      setHook(""); setPattern(""); setNiche("");
      toast.success("Added to swipe file");
      onChange();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const remove = async (id) => {
    if (!canEdit) return;
    try { await api.delete(`/patterns/swipes/${id}`); onChange(); }
    catch (error) { toast.error(formatError(error)); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
    toast.success("Hook copied");
  };

  return (
    <div className="space-y-4" data-testid="swipe-file">
      {canEdit && <Card className="p-4">
        <form onSubmit={add} className="flex flex-col sm:flex-row gap-2">
          <Input value={hook} onChange={(e) => setHook(e.target.value)} placeholder="Add your own hook…" required minLength={2} className="flex-1" data-testid="swipe-hook-input" />
          <Input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="Pattern" className="sm:w-44" data-testid="swipe-pattern-input" />
          <Input value={niche} onChange={(e) => setNiche(e.target.value)} placeholder="Niche" className="sm:w-36" data-testid="swipe-niche-input" />
          <Button type="submit" disabled={hook.trim().length < 2} data-testid="swipe-add-button"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </form>
      </Card>}
      {swipes.length === 0 ? (
        <EmptyState testid="swipe-empty" title="Aucun hook dans le Swipe File" description="Consultez la bibliothèque pour trouver des idées." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {swipes.map((s) => (
            <Card key={s.id} className="p-4 flex gap-3" data-testid={`swipe-item-${s.id}`}>
              {s.thumbnail && <MediaImage src={s.thumbnail} alt="" demo={explicitDemoMedia(s)} showLabel={false} className="h-16 w-12 rounded object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold leading-snug">“{s.hook_text}”</div>
                <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground flex-wrap">
                  {s.pattern && <span className="rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">{s.pattern}</span>}
                  {s.niche && <span>{s.niche}</span>}
                  {s.platform && <PlatformBadge platform={s.platform} size="xs" />}
                  {s.creator_handle && <span>{s.creator_handle}</span>}<span>Archive · {s.performance_context?.handle ? `référence ${s.performance_context.handle}` : "contexte historique non vérifié"}</span>
                  <span className="ml-auto">{timeAgo(s.created_at)}</span>
                </div>
                {s.video_id && <Link to={`/videos/${s.video_id}`} className="text-xs text-primary hover:underline mt-1 inline-block">Open video</Link>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => copy(s.hook_text)} className="text-muted-foreground hover:text-foreground p-1" aria-label="Copy" data-testid={`swipe-copy-${s.id}`}><Copy className="h-4 w-4" /></button>
                {canEdit && <button onClick={() => remove(s.id)} className="text-muted-foreground hover:text-[hsl(var(--rose))] p-1" aria-label="Delete" data-testid={`swipe-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
